import styled from 'styled-components';
import { ProductDTO } from 'src/models/pim';
import {
  ProductAttributeCode,
  ProductAttributeCollection,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { FC } from 'react';
import { Heading } from 'src/components/common/Heading';
import { media } from 'src/styles/designs';
import { head } from 'lodash';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { Paragraph } from 'src/components/common/typography/Text';
import { StickyMenuSection } from 'src/components/common/StickyMenu/StickyMenuSection';

export interface ProductPageIngredientsListSectionProps {
  product: ProductDTO;
  descriptionCode?: ProductAttributeCode;
  dictionaryTitleCode?: DictionaryTitlesCode;
  titleCode?: ProductAttributeCode;
}

const Section = styled(StickyMenuSection)`
  padding: 0 0 48px;
  background: ${({ theme }) => theme.palette.background};

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 16px 0 58px;
  }
`;

export const ProductPageIngredientsListSection: FC<
  ProductPageIngredientsListSectionProps
> = ({ product, titleCode, dictionaryTitleCode, descriptionCode }) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute } = useProductAttributes(product.attributes);

  const [dictionaryTitle] = useTitlesDictionary(dictionaryTitleCode);

  const title =
    head(
      getProductAttribute(
        titleCode as unknown as ProductAttributeCollection.TextValues,
      ),
    ) ?? dictionaryTitle;

  const description = head(
    getProductAttribute(
      descriptionCode as unknown as ProductAttributeCollection.TextValues,
    ),
  );

  if (!title || !description || !translateFn(description).value) {
    return null;
  }

  return (
    <Section id="ingredients" label={translateFn(title).value} threshold={0.5}>
      <ConstraintContainer>
        <Heading>
          <Markdown>{translateFn(title).value}</Markdown>
        </Heading>

        {description && (
          <Paragraph>
            <Markdown>{translateFn(description).value}</Markdown>
          </Paragraph>
        )}
      </ConstraintContainer>
    </Section>
  );
};
